import { useEffect, useState } from "react";
import { fetchActiveDocument } from "src/data/legalDocument";
import { parseStructuredDoc } from "src/utils/legal/acceptanceCopy";
import { captureError } from "src/utils/errorReporting";
import { useLang } from "src/i18n";

/**
 * Fetch a DB-sourced acceptance-copy doc once on mount — and again whenever the
 * active UI language changes (backend falls back to the Finnish row when no
 * translation is published yet, mirroring useActiveLegalDocument) — and return
 * its parsed content with a bundled fallback. Used by the Gate-1 wizard consent
 * and the Gate-2 offer-acceptance copy.
 *
 * Returns `{ copy, version }`:
 *   - `copy`     the parsed JSON object, or `fallback` (same reference) on any
 *                fetch/parse failure so the legal text always renders.
 *   - `version`  the active DB row's version, or `fallbackVersion` on failure
 *                (Gate-1 stamps this on pumppuRequest.termsVersion; Gate-2
 *                callers ignore it).
 *
 * A parse-fallback (content present but unparseable) and an unexpected fetch
 * error are both reported to Sentry via captureError. A 404 (no active doc) is
 * expected — it falls back silently, matching the repo's 401/404-skip convention.
 *
 * `fallback` MUST be memoized by the caller (it's a `t`-derived object — see
 * src/utils/legal/acceptanceCopy.js's gate1ContentFallback/gate2Fallback) since
 * it's a dependency here: an unmemoized fresh object every render would refetch
 * forever, and a language change legitimately needs to refetch/re-derive it.
 */
export function useAcceptanceCopy(typeName, fallback, fallbackVersion = null) {
    const { lang } = useLang();
    const [state, setState] = useState({ copy: fallback, version: fallbackVersion });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const doc = await fetchActiveDocument(typeName, { language: lang });
                if (cancelled) return;
                const copy = parseStructuredDoc(doc?.markdownContent, fallback);
                if (copy === fallback) {
                    captureError(new Error(`${typeName} content failed to parse — using fallback`), {
                        tags: { entity: "legalDocument", typeName, swallowed: true },
                    });
                }
                setState({ copy, version: doc?.version || fallbackVersion });
            } catch (err) {
                // 404 = no active doc for this type — expected, fall back silently.
                if (err?.status !== 404) {
                    captureError(err, {
                        tags: { entity: "legalDocument", typeName, operation: "fetch", swallowed: true },
                    });
                }
                if (cancelled) return;
                // MUST update state here, not just report/log: `useState`'s initializer
                // only runs at mount, so without this the state keeps whatever the LAST
                // successful fetch set (or the mount-time fallback) forever. Concretely:
                // mount in English (English fallback in state) → user switches to
                // Finnish → this fetch 404s/fails (expected, e.g. no translated row yet)
                // → without this line, state never updates and a Finnish user silently
                // reads the English fallback consent text. `fallback`/`fallbackVersion`
                // are re-derived by the caller per the active `lang` (that's why they're
                // in the dependency array below), so re-applying them here is what keeps
                // the failure path correct for the CURRENT language. Do not "simplify"
                // this back to a bare Sentry report.
                setState({ copy: fallback, version: fallbackVersion });
            }
        })();
        return () => { cancelled = true; };
    }, [typeName, fallback, fallbackVersion, lang]);
    return state;
}
